import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link2, CheckCircle, XCircle, AlertTriangle, MinusCircle, RefreshCw, Settings, Eye, EyeOff } from 'lucide-react';
import { integracoesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Componente genérico de Integrações para a página de detalhes do cliente.
 * Renderiza uma linha por item do catálogo, com:
 * - Badge de status (não configurada / conectada / erro / desabilitada)
 * - Toggle de habilitação por cliente (D9 e D10)
 * - Modal de configuração gerado a partir dos campos declarativos do catálogo
 *
 * Sem nenhum if por provedor — o catálogo é a fonte de verdade.
 */
const IntegracoesCliente = ({ clienteId, clienteNome }) => {
  const { darkMode } = useTheme();
  const [catalogo, setCatalogo] = useState([]);
  const [integracoes, setIntegracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(null); // key do provedor
  const [formData, setFormData] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(null); // id da integração
  const [camposVisiveis, setCamposVisiveis] = useState({}); // { campo_nome: bool }

  const loadData = useCallback(async () => {
    try {
      const [catRes, intRes] = await Promise.all([
        integracoesAPI.getCatalogo(),
        integracoesAPI.getTodasDoCliente(clienteId),
      ]);
      setCatalogo(catRes.data || []);
      setIntegracoes(intRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar integrações:', error);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Retorna a integração ativa/configurada para um provedor
  const getIntegracaoProvedor = (provedorKey) => {
    return integracoes.find(i => i.provedor === provedorKey);
  };

  // Status badge genérico
  const getStatusInfo = (item) => {
    const integ = getIntegracaoProvedor(item.key);
    if (!integ) {
      return { label: 'Não configurada', cor: 'gray', icon: MinusCircle };
    }
    if (integ.habilitado === false) {
      return { label: 'Desabilitada', cor: 'yellow', icon: AlertTriangle };
    }
    if (integ.status === 'erro' || integ.ultimo_erro) {
      return { label: integ.ultimo_erro || 'Erro', cor: 'red', icon: XCircle };
    }
    if (integ.status === 'ativo') {
      return { label: 'Conectada', cor: 'green', icon: CheckCircle };
    }
    return { label: integ.status || 'Desconhecido', cor: 'gray', icon: MinusCircle };
  };

  const badgeClasses = (cor) => {
    const map = {
      green: darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700',
      red: darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700',
      yellow: darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
      gray: darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500',
    };
    return map[cor] || map.gray;
  };

  // Toggle de habilitação
  const handleToggle = async (provedorKey, habilitado) => {
    try {
      await integracoesAPI.habilitar(clienteId, provedorKey, habilitado);
      toast.success(habilitado ? 'Integração habilitada' : 'Integração desabilitada');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar habilitação');
    }
  };

  // Testar conexão genérico — usa { ok, mensagem }
  const handleTestar = async (integracaoId) => {
    setTestando(integracaoId);
    try {
      const res = await integracoesAPI.testar(integracaoId);
      if (res.data?.ok) {
        toast.success(res.data.mensagem || 'Conexão OK!');
      } else {
        toast.error(res.data?.mensagem || 'Falha na conexão');
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestando(null);
    }
  };

  // Sync genérico
  const handleSync = async (integracaoId) => {
    try {
      const res = await integracoesAPI.sync(integracaoId);
      if (res.data?.error) {
        toast.error(res.data.error);
      } else {
        toast.success(res.data?.message || 'Sincronização iniciada');
      }
      loadData();
    } catch (error) {
      toast.error('Erro ao sincronizar');
    }
  };

  // Abrir modal de configuração — gera formulário a partir dos campos
  const abrirModalConfig = (item) => {
    const integ = getIntegracaoProvedor(item.key);
    // Preencher form com valores existentes (campos secretos sempre vazios — nunca retornam)
    const defaults = {};
    item.campos.forEach(campo => {
      defaults[campo.nome] = integ?.config?.[campo.nome] || '';
    });
    setFormData(defaults);
    setCamposVisiveis({});
    setModalAberto(item.key);
  };

  // Salvar configuração via API de criar ou configurar
  const handleSalvarConfig = async (item) => {
    // Validar campos obrigatórios
    const camposFaltando = item.campos
      .filter(c => c.obrigatorio && !formData[c.nome])
      .map(c => c.label);

    if (camposFaltando.length > 0) {
      toast.error(`Preencha: ${camposFaltando.join(', ')}`);
      return;
    }

    setSalvando(true);
    try {
      const integ = getIntegracaoProvedor(item.key);
      if (integ) {
        // Atualizar existente
        await integracoesAPI.configurar(integ.id, formData);
      } else {
        // Criar nova
        await integracoesAPI.criar({
          cliente_id: clienteId,
          provedor: item.key,
          nome: `${item.nome} - ${clienteNome}`,
          credenciais: formData,
        });
      }
      toast.success('Configuração salva com sucesso');
      setModalAberto(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSalvando(false);
    }
  };

  // OAuth2 (Bling): redirect para URL de autorização
  const handleOAuth = async (item) => {
    try {
      const res = await integracoesAPI.autorizarBling(clienteId, `${item.nome} - ${clienteNome}`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
        toast.info('Autorize o acesso e volte aqui');
      } else {
        toast.error(res.data?.error || 'Erro ao gerar URL de autorização');
      }
    } catch (error) {
      toast.error('Erro ao iniciar autorização');
    }
  };

  // Ação principal do botão de configurar — depende do tipo de auth
  const handleConfigurar = (item) => {
    if (item.auth === 'oauth2') {
      handleOAuth(item);
    } else {
      abrirModalConfig(item);
    }
  };

  // Remover integração
  const handleRemover = async (integracaoId) => {
    try {
      await integracoesAPI.deletarIntegracao(integracaoId);
      toast.success('Integração removida');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover integração');
    }
  };

  if (loading) {
    return (
      <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-4">
          <Link2 size={20} className={darkMode ? 'text-white' : 'text-gray-900'} />
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Integrações</h2>
        </div>
        <div className="animate-pulse space-y-3">
          <div className={`h-16 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}></div>
          <div className={`h-16 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-4">
          <Link2 size={20} className={darkMode ? 'text-white' : 'text-gray-900'} />
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Integrações</h2>
        </div>

        <div className="space-y-3">
          {catalogo.map(item => {
            const integ = getIntegracaoProvedor(item.key);
            const statusInfo = getStatusInfo(item);
            const StatusIcon = statusInfo.icon;

            return (
              <div
                key={item.key}
                className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Nome e Provedor */}
                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                      darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {item.nome}
                    </span>
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {item.descricao}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Badge de status */}
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badgeClasses(statusInfo.cor)}`}>
                      <StatusIcon size={12} />
                      <span className="max-w-[200px] truncate">{statusInfo.label}</span>
                    </span>

                    {/* Toggle habilitação (só aparece se já configurada) */}
                    {integ && (
                      <button
                        onClick={() => handleToggle(item.key, integ.habilitado === false)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          integ.habilitado !== false ? 'bg-nomoz-orange' : darkMode ? 'bg-zinc-600' : 'bg-gray-300'
                        }`}
                        title={integ.habilitado !== false ? 'Desabilitar' : 'Habilitar'}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          integ.habilitado !== false ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </button>
                    )}

                    {/* Ações */}
                    {integ && (
                      <>
                        <button
                          onClick={() => handleTestar(integ.id)}
                          disabled={testando === integ.id}
                          className={`px-3 py-1.5 text-xs rounded border ${
                            darkMode ? 'border-gray-600 text-gray-300 hover:bg-zinc-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          } ${testando === integ.id ? 'opacity-50' : ''}`}
                        >
                          {testando === integ.id ? 'Testando...' : 'Testar'}
                        </button>
                        <button
                          onClick={() => handleSync(integ.id)}
                          className="gradient-button px-3 py-1.5 text-xs text-white rounded flex items-center gap-1"
                        >
                          <RefreshCw size={12} />
                          Sync
                        </button>
                      </>
                    )}

                    {/* Botão configurar/conectar */}
                    <button
                      onClick={() => handleConfigurar(item)}
                      className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                        integ
                          ? darkMode ? 'border border-gray-600 text-gray-300 hover:bg-zinc-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          : 'gradient-button text-white'
                      }`}
                    >
                      <Settings size={12} />
                      {integ ? 'Configurar' : 'Conectar'}
                    </button>
                  </div>
                </div>

                {/* Informações adicionais quando configurada */}
                {integ && (
                  <div className="mt-2 flex items-center gap-4">
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Último sync: {integ.ultimo_sync ? new Date(integ.ultimo_sync).toLocaleString('pt-BR') : 'Nunca'}
                    </p>
                    <button
                      onClick={() => handleRemover(integ.id)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Desconectar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de configuração gerado a partir dos campos declarativos */}
      {modalAberto && (() => {
        const item = catalogo.find(c => c.key === modalAberto);
        if (!item) return null;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalAberto(null)}>
            <div
              className={`w-full max-w-md rounded-lg shadow-xl p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}
            >
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Configurar {item.nome}
              </h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {item.descricao}
              </p>

              {/* Formulário gerado dinamicamente a partir de item.campos */}
              <div className="space-y-4">
                {item.campos.map(campo => (
                  <div key={campo.nome}>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {campo.label} {campo.obrigatorio && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                      <input
                        type={campo.secreto && !camposVisiveis[campo.nome] ? 'password' : campo.tipo === 'password' ? 'password' : campo.tipo}
                        value={formData[campo.nome] || ''}
                        onChange={(e) => setFormData({ ...formData, [campo.nome]: e.target.value })}
                        placeholder={campo.secreto ? '••••••••' : `Digite ${campo.label.toLowerCase()}`}
                        className={`w-full rounded-lg px-4 py-2 border ${
                          darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'
                        }`}
                      />
                      {/* Botão olho para campos secretos */}
                      {campo.secreto && (
                        <button
                          type="button"
                          onClick={() => setCamposVisiveis({ ...camposVisiveis, [campo.nome]: !camposVisiveis[campo.nome] })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                        >
                          {camposVisiveis[campo.nome] ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Botões do modal */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setModalAberto(null)}
                  className={`px-4 py-2 rounded-lg text-sm ${
                    darkMode ? 'border border-gray-600 text-gray-300 hover:bg-zinc-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleSalvarConfig(item)}
                  disabled={salvando}
                  className="gradient-button px-4 py-2 text-sm text-white rounded-lg"
                >
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default IntegracoesCliente;
